import npis from "./npis.json";
import { MonitorService } from "./captureException";

/** Levenshtein distance between two strings (used for fuzzy NPI name matching). */
export function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[m][n];
}

export const REQUIRED_MATCHES = 2;
export const FUZZY_MATCH_THRESHOLD = 0.3;

/**
 * Resolve NPI for a given full name using fuzzy matching against the NPI registry.
 * @param {string} fullName - Provider full name
 * @param {string} [noteType] - e.g. "BELUGA_HEALTH"; when set, reports to MonitorService if no match
 * @returns {string} NPI number or "NPI not found"
 */
export function getNpiFromName(fullName, noteType) {
  if (!fullName) return "NPI not found";

  const normalize = (str) =>
    str
      .toLowerCase()
      .replace(/[^a-z\s]/gi, "")
      .replace(/\s+/g, " ")
      .trim();

  const nameTokens = normalize(fullName).split(" ");

  const foundEntry = npis.find((entry) => {
    const entryTokens = normalize(entry.name).split(" ");
    let matches = 0;
    nameTokens.forEach((inputToken) => {
      for (const entryToken of entryTokens) {
        const distance = levenshtein(inputToken, entryToken);
        const threshold = Math.floor(
          Math.max(inputToken.length, entryToken.length) * FUZZY_MATCH_THRESHOLD
        );
        if (distance <= threshold) {
          matches++;
          break;
        }
      }
    });
    return matches >= REQUIRED_MATCHES;
  });

  if (!foundEntry && noteType === "BELUGA_HEALTH") {
    MonitorService.captureException(new Error(`NPI not found for name: ${fullName}`));
    return "NPI not found";
  }

  return foundEntry?.npi || "NPI not found";
}
